import React from "react";
import { motion } from "framer-motion";
import { Star, ArrowDownRight, ChevronDown } from "lucide-react";
import { FaGoogle } from "react-icons/fa";
import { IMAGES, RATING, REVIEWS_COUNT } from "@/lib/site";
import { WhatsappButton } from "@/components/site/WhatsappButton";

const line = {
  hidden: { y: "110%", opacity: 0 },
  show: (i) => ({
    y: "0%",
    opacity: 1,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.1 },
  }),
};

const Line = ({ children, i }) => (
  <span className="reveal-mask">
    <motion.span
      variants={line}
      custom={i}
      initial="hidden"
      animate="show"
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-between overflow-hidden pt-32 pb-12 md:pt-36 md:pb-16"
    >
      {/* Soft Ambient Background Lights */}
      <div className="pointer-events-none absolute -top-24 -right-16 h-96 w-96 rounded-full bg-clinic-pink/25 blur-3xl gpu-accel" />
      <div className="pointer-events-none absolute top-1/2 -left-28 h-80 w-80 rounded-full bg-clinic-pinkSoft/60 blur-3xl gpu-accel" />
      
      <div className="mx-auto max-w-7xl w-full px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center my-auto">
        {/* Left Column: Text & CTAs */}
        <div className="lg:col-span-7 relative z-10">

          {/* Rating Badge */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="hero-rating-badge"
            className="inline-flex items-center gap-2.5 rounded-full border border-clinic-rose/30 bg-white/70 backdrop-blur-md px-4 py-1.5 mb-7 shadow-sm"
          >
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-clinic-gold text-clinic-gold" />
              ))}
            </div>
            <span className="font-sans text-xs font-medium tracking-wide text-clinic-ink">
              {RATING} · {REVIEWS_COUNT} avaliações 5 estrelas no Google
            </span>
          </motion.div>

          {/* Heading */}
          <h1 className="font-serif text-clinic-ink font-medium tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
            <Line i={0}>Beleza que nasce</Line>
            <Line i={1}>
              do <em className="italic text-clinic-rose font-normal">cuidado</em> com você
            </Line>
          </h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-7 max-w-xl font-sans text-base md:text-lg text-clinic-muted leading-relaxed"
          >
            Estética facial, corporal e saúde em um só lugar. Um espaço acolhedor
            em Jundiaí com atendimento personalizado para realçar a sua beleza
            natural com segurança e sofisticação.
          </motion.p>

          {/* Action CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-4 sm:gap-6"
          >
            <WhatsappButton label="Agendar Consulta" testId="hero-whatsapp-cta" />
            <a
              href="#sobre"
              className="group inline-flex items-center gap-2 font-sans text-sm font-medium text-clinic-ink/75 hover:text-clinic-ink transition-colors py-2"
            >
              <span>Conheça a clínica</span>
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1 text-clinic-rose" />
            </a>
          </motion.div>
        </div>

        {/* Right Column: Hero Visual Composition */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="relative aspect-[4/5] w-full overflow-hidden rounded-[2rem] shadow-soft-lg border-2 border-white/60 bg-clinic-pinkSoft/40"
          >
            <img
              src={IMAGES.hero}
              alt="Clínica Lília Targa — Cuidado e estética facial"
              loading="eager"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-clinic-ink/30 via-transparent to-transparent" />
          </motion.div>

          {/* Floating Rating Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20, x: -15 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="glass-card absolute -left-3 sm:-left-8 bottom-6 sm:bottom-8 rounded-2xl border border-clinic-line/80 shadow-soft-lg p-4 sm:p-5 max-w-[210px]"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <FaGoogle className="h-4 w-4 text-clinic-rose" />
              <span className="font-serif text-3xl font-medium text-clinic-ink leading-none">{RATING}</span>
            </div>
            <div className="flex items-center gap-0.5 mb-1.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3 w-3 fill-clinic-gold text-clinic-gold" />
              ))}
            </div>
            <p className="font-sans text-xs text-clinic-muted leading-tight">
              Excelência avaliada por {REVIEWS_COUNT} pacientes em Jundiaí
            </p>
          </motion.div>
        </div>
      </div>

      {/* Subtle Scroll Down Prompt */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="mx-auto hidden md:flex flex-col items-center gap-1.5 pt-4 text-clinic-muted/70 hover:text-clinic-ink transition-colors cursor-pointer"
        onClick={() => {
          const el = document.getElementById("sobre");
          if (el && window.lenis) window.lenis.scrollTo(el, { offset: -70 });
        }}
      >
        <span className="font-sans text-[0.65rem] uppercase tracking-[0.3em]">Explorar</span>
        <motion.div
          animate={{ y: [0, 5, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ChevronDown className="h-4 w-4" />
        </motion.div>
      </motion.div>
    </section>
  );
};

